"use client";

import { useEffect, useRef } from "react";
import {
  createChart,
  CandlestickSeries,
  LineStyle,
  ColorType,
  CrosshairMode,
} from "lightweight-charts";
import type { IChartApi, ISeriesApi, IPriceLine, Time } from "lightweight-charts";
import { useChartData } from "@/pages/trade/components/chart/chart-data-context";

interface ChartSwitcherProps {
  symbol?: string;
  timeFrame?: string;
  onChartContextReady?: (context: any) => void;
  showExpiry?: boolean;
  expiryMinutes?: number;
  orders?: any[];
  marketType?: string;
  onPriceUpdate?: (price: number) => void;
  metadata?: any;
}

function msToChartTime(ms: number): Time {
  return (ms / 1000) as Time;
}

export default function ChartSwitcher({
  symbol = "BTC/USDO",
  onChartContextReady,
  onPriceUpdate,
}: ChartSwitcherProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const priceLineRef = useRef<IPriceLine | null>(null);
  const { candleData, currentPrice } = useChartData();

  // Initialize chart
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const chart = createChart(container, {
      layout: {
        background: { type: ColorType.Solid, color: "#000000" },
        textColor: "#d1d4dc",
        fontSize: 10,
      },
      grid: {
        vertLines: { color: "#1a1a1a" },
        horzLines: { color: "#1a1a1a" },
      },
      rightPriceScale: {
        borderColor: "#1a1a1a",
      },
      timeScale: {
        borderColor: "#1a1a1a",
        timeVisible: true,
        secondsVisible: false,
      },
      crosshair: {
        mode: CrosshairMode.Normal,
      },
      autoSize: true,
    });

    const series = chart.addSeries(CandlestickSeries, {
      upColor: "#26a69a",
      downColor: "#ef5350",
      borderUpColor: "#26a69a",
      borderDownColor: "#ef5350",
      wickUpColor: "#26a69a",
      wickDownColor: "#ef5350",
    });

    chartRef.current = chart;
    seriesRef.current = series;

    return () => {
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      priceLineRef.current = null;
    };
  }, []);

  // Update candle data
  useEffect(() => {
    const series = seriesRef.current;
    if (!series) return;

    const data = candleData;
    if (!data || data.length === 0) {
      // No real data available — clear the chart instead of showing fake candles
      series.setData([]);
      if (onChartContextReady) {
        onChartContextReady({
          candleData: [],
          chartType: "candlestick",
          currentPrice: 0,
        });
      }
      return;
    }

    const mapped = data.map((c: any) => ({
      time: msToChartTime(c.time || new Date(c.timestamp).getTime()),
      open: Number(c.open),
      high: Number(c.high),
      low: Number(c.low),
      close: Number(c.close),
    }));

    series.setData(mapped);
    const chart = chartRef.current;
    if (chart) {
      chart.timeScale().fitContent();
    }

    const lastClose = mapped.length > 0 ? mapped[mapped.length - 1].close : 0;
    if (onChartContextReady) {
      onChartContextReady({
        candleData: data,
        chartType: "candlestick",
        currentPrice: lastClose,
      });
    }
    if (onPriceUpdate && lastClose > 0) {
      onPriceUpdate(lastClose);
    }
  }, [candleData, onChartContextReady, onPriceUpdate]);

  // Update current price line
  useEffect(() => {
    const series = seriesRef.current;
    if (!series || currentPrice <= 0) return;

    if (priceLineRef.current) {
      priceLineRef.current.applyOptions({
        price: currentPrice,
        title: currentPrice.toFixed(2),
      });
    } else {
      priceLineRef.current = series.createPriceLine({
        price: currentPrice,
        color: "#2196f3",
        lineStyle: LineStyle.Dashed,
        lineWidth: 1,
        axisLabelVisible: true,
        title: currentPrice.toFixed(2),
      });
    }
  }, [currentPrice]);

  return (
    <div ref={containerRef} className="w-full h-full relative">
      <div
        style={{
          position: "absolute",
          top: 4,
          left: 8,
          color: "#ffffff",
          fontWeight: 700,
          fontSize: 12,
          zIndex: 2,
          pointerEvents: "none",
        }}
      >
        {symbol}
      </div>
    </div>
  );
}
